import { TrackedChange } from 'models/changes'
import { MutableRefObject, useCallback } from 'react'
import {
    ScrollToContent,
    useScrollStoreShallowSelector,
} from '../store/scrollStore'
import { useRequestLifeCycleHandler } from './useRequestLifeCycleHandler'
import { useSafeDelay } from './useSafeDelay'
import { useScrollRequestExecutor } from './useScrollRequestExecutor'
import { useScrollRequestValidator } from './useScrollRequestValidator'

interface SetPendingScrollRequest {
    scrollToId: string
    correspondingTrackChange: TrackedChange
    timestamp: number
}

/**
 * A custom hook to handle scrolling to a requested tree node.
 * @param scrollToContent - The current scroll content.
 * @param scrollToElementRef - A ref to the function that scrolls to a specific element by ID.
 * @param clearScrollToContentRef - A ref to the function that clears the scroll to content.
 * @param setPendingScrollRequest - A function to set a pending scroll request.
 * @returns A function that handles the scroll request.
 */
export const useHandleScrollToRequestedTreeNode = (
    scrollToContent: ScrollToContent,
    scrollToElementRef: MutableRefObject<(id: string, additionalIds?: string[]) => Promise<void>>,
    clearScrollToContentRef: MutableRefObject<() => void>,
    setPendingScrollRequest: (req: SetPendingScrollRequest) => void
) => {
    // Delegate responsibilities to focused hooks
    const {
        startNewRequest,
        isCurrentRequest,
        isMounted,
    } = useRequestLifeCycleHandler()
    const {
        scrollToId,
        correspondingTrackChange,
        isRequestValid,
    } = useScrollRequestValidator(scrollToContent)

    const { isHandlingChangeTab } = useScrollStoreShallowSelector((state) => ({
        isHandlingChangeTab: state.isHandlingChangeTab,
    }))

    const safeDelay = useSafeDelay(isMounted)

    const { executeScrollRequest } = useScrollRequestExecutor({
        scrollToContent,
        scrollToElementRef,
        clearScrollToContentRef,
        setPendingScrollRequest,
        isHandlingChangeTab,
        safeDelay,
        isCurrentRequest,
    })

    return useCallback(async (): Promise<void> => {
        // Early validation returns
        if (!isRequestValid) {
            clearScrollToContentRef.current()
            return
        }

        // Start new request (cancels any previous)
        const requestId = startNewRequest()

        // Execute the scroll request
        await executeScrollRequest(
            requestId,
            scrollToId!,
            correspondingTrackChange!
        )
    }, [
        isRequestValid,
        scrollToId,
        correspondingTrackChange,
        startNewRequest,
        executeScrollRequest,
    ])
}
